/**
 * React Query hook + types for Retail > OMNI Performance (/retail/performance).
 *
 * - useOmni(asOf)          GET /v1/retail/omni?as_of=YYYY-MM-DD
 * - downloadOmniXlsx(asOf) GET /v1/retail/omni/export?as_of=YYYY-MM-DD
 *
 * The OMNI daily report is the native Cockpit rebuild of the QuickSight
 * "OMNI Performance Recap" email (spec: 00_omni-performance-reporting.md). This
 * file matches the shipped api contract (api #376) and is the single reconcile
 * point for any future contract drift: adjust the types + `flattenOmni`
 * normalizer + `OMNI_SECTION_COLUMNS`/`blockFor` here and the page follows.
 *
 * Shipped response shape for GET /v1/retail/omni?as_of=YYYY-MM-DD
 * (as_of defaults to the latest complete business day). The response is a flat
 * list of stores plus a fleet-wide grand total, with no region/district grouping
 * (per Franco: list all stores, leave regions and districts out):
 *
 *   {
 *     as_of, fiscal_month_start, fiscal_year_start,
 *     scope: "fleet" | "store", computed_at,
 *     windows: { yesterday|wtd|mtd|ytd: { ty|lyWeek: {start,end} } } | null,
 *     stores: [ { store_no, store_name, region, district, sections } ],
 *     grand_total: <sections>
 *   }
 *
 * `stores` is a flat array ordered by store_no; each store nests its metric
 * sections under `.sections`. `region` and `district` remain on the store record
 * for reference but are not rendered. `grand_total` is a `<sections>` object
 * directly. `flattenOmni` emits one row per store followed by a single grand-total
 * row. It degrades to an empty list on any 404 / 503 / shape mismatch.
 *
 * A `<sections>` object has five keys, each with `yesterday`/`wtd`/`mtd`/`ytd`
 * blocks:
 *   - sales:        { ty, ly_week, comp_week }
 *   - transactions: { ty, ly_week, comp_week }
 *   - traffic:      { ty, ly_week, comp_week, days_with_traffic,
 *                     conversion: { ty, ly_week, comp_week } }
 *                   (days_with_traffic = count of days with traffic data in the
 *                    period; conversion counts only those days)
 *   - units:        { ty, ly_week, comp_week }
 *   - dpt_upt:      { dpt: { ty, ly_week, comp_week },
 *                     upt: { ty, ly_week, comp_week } }
 *
 * Field semantics: `ly_week` = the same weekday last year (date - 364 days);
 * `comp_week` = ty/ly - 1 (decimal ratio, null when ly = 0).
 *
 * The same-calendar-date basis (`ly_cal` / `comp_cal`) and the sales `plan` /
 * `bgt_pct` figures were removed on 2026-08-12, and a year-to-date period added,
 * at William's and Franco's direction: the report should say one thing, did the
 * store beat last year.
 *
 * RBAC: access is enforced server-side in-handler. A caller with
 * retail.omni.read gets the fleet; otherwise the response is scoped to the
 * caller's store_user-mapped stores; neither yields a 403. The UI adds a
 * matching client gate (retail.omni.read OR stores:store) for a clean
 * access-denied state, and renders whatever the API returns.
 *
 * Resilience: `retry: false`, stable empty defaults, and a tolerant read path
 * so the page degrades to a "not available yet" state on any 404 / 503 / shape
 * mismatch rather than crashing.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPut } from './api';
import { API_BASE } from './apiBase';
import { formatComp, formatDollars, formatDpt } from './useRetailStores';

export const OMNI_API_BASE = `${API_BASE}/v1/retail/omni`;

// ---------------------------------------------------------------------------
// Types (the shipped api #376 contract; reconcile here on any drift)
// ---------------------------------------------------------------------------

export type OmniSectionKey = 'sales' | 'transactions' | 'traffic' | 'units' | 'dpt_upt';
export type OmniPeriodKey = 'yesterday' | 'wtd' | 'mtd' | 'ytd';

/**
 * The core metric cell shared by every section and sub-metric. `ty` is this
 * year; `ly_week` is the same weekday last year (date - 364d); `comp_week` is
 * the decimal comp ratio between them (0.05 = +5%). All fields nullable.
 */
export type OmniMetricCell = {
  ty: number | null;
  ly_week: number | null;
  comp_week: number | null;
};

/**
 * Traffic adds a nested conversion metric (transactions / traffic) and a
 * `days_with_traffic` count. Conversion is computed only over days that had
 * traffic data, so `days_with_traffic` tells the reader how many days of the
 * period the conversion figure actually covers (partial-coverage periods are
 * not comparable). Null when the count is unknown.
 */
export type OmniTrafficCell = OmniMetricCell & {
  conversion: OmniMetricCell | null;
  days_with_traffic: number | null;
};

/** DPT & UPT is two nested metrics; the section has no top-level metric cell. */
export type OmniDptUptCell = {
  dpt: OmniMetricCell | null;
  upt: OmniMetricCell | null;
};

/** A section's four period blocks. Any period may be absent (renders "--"). */
export type OmniPeriodsOf<B> = {
  yesterday?: B | null;
  wtd?: B | null;
  mtd?: B | null;
  ytd?: B | null;
};

/** The five sections for one row (store or grand total). */
export type OmniSections = {
  sales?: OmniPeriodsOf<OmniMetricCell> | null;
  transactions?: OmniPeriodsOf<OmniMetricCell> | null;
  traffic?: OmniPeriodsOf<OmniTrafficCell> | null;
  units?: OmniPeriodsOf<OmniMetricCell> | null;
  dpt_upt?: OmniPeriodsOf<OmniDptUptCell> | null;
};

/**
 * One store row. `region` / `district` are kept on the record for reference but
 * are not rendered on the web view (per Franco: list all stores, no grouping).
 */
export type OmniStore = {
  store_no: string;
  store_name: string;
  city?: string | null;
  state?: string | null;
  region?: string | null;
  district?: string | null;
  sections: OmniSections;
};

/** An inclusive calendar-date range, YYYY-MM-DD in and out. */
export type OmniDateWindow = { start: string; end: string };

/**
 * The two bases behind one period column-group: this-year (`ty`) and the same
 * weekday(s) last year, shifted 364 days (`lyWeek`). Mirrors `BlockWindows` in
 * the api repo's tools/retail/src/omni-metrics.ts, which is where this logic is
 * computed.
 */
export type OmniBlockWindows = {
  ty: OmniDateWindow;
  lyWeek: OmniDateWindow;
};

/**
 * The date windows behind each period column-group (Yesterday / WTD / MTD /
 * YTD), echoed by the API so the UI never recomputes the fiscal-week, fiscal-
 * year or 364-day-shift logic that lives server-side. Any period may be absent
 * (an older API, or no data at all), in which case a header falls back to its
 * plain label.
 */
export type OmniWindows = Partial<Record<OmniPeriodKey, OmniBlockWindows | null>> | null;

/**
 * How complete the data behind `as_of` is. `retail_sales_daily` is fed by two
 * loaders minutes apart (the CounterPoint mirror for the 47 physical stores, the
 * Shopify sync for the two online ones), so the API only accepts the newest
 * business day when at least half the active physical stores hold a row for it,
 * and otherwise steps back a day. `note` is a plain-English warning, null when
 * the day loaded cleanly. Optional so the page still renders against an older API.
 */
export type OmniFreshness = {
  physical_stores_reporting: number;
  physical_stores_expected: number;
  coverage_ok: boolean;
  fell_back: boolean;
  note: string | null;
};

export type OmniResponse = {
  as_of: string;
  fiscal_month_start?: string | null;
  /** First day of the fiscal year behind the year-to-date column. */
  fiscal_year_start?: string | null;
  scope?: 'fleet' | 'store';
  computed_at?: string | null;
  freshness?: OmniFreshness | null;
  windows?: OmniWindows;
  stores: OmniStore[];
  grand_total?: OmniSections | null;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOmni(asOf: string | null) {
  const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : '';
  const query = useQuery<OmniResponse, Error>({
    queryKey: ['omni', asOf ?? ''],
    queryFn: () => apiGet<OmniResponse>(`${OMNI_API_BASE}${qs}`),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return {
    asOf: query.data?.as_of ?? null,
    computedAt: query.data?.computed_at ?? null,
    freshness: query.data?.freshness ?? null,
    scope: query.data?.scope ?? null,
    windows: query.data?.windows ?? null,
    stores: query.data?.stores ?? [],
    grandTotal: query.data?.grand_total ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

// ---------------------------------------------------------------------------
// Subscription: daily email delivery of this report
// ---------------------------------------------------------------------------

/**
 * The caller's subscription to the daily OMNI Performance email.
 *
 *   GET /v1/retail/omni/subscription -> OmniSubscription
 *   PUT /v1/retail/omni/subscription  body { subscribed, cadence? } -> OmniSubscription
 *
 * `subscribed` is the live on/off state; `status` is the durable record
 * ('active' when receiving the email, 'unsubscribed' after opting out).
 * `auto_enrolled` is true for a caller (e.g. a Store Manager) who is subscribed
 * by default and may opt out. `scope` mirrors the report scope the email
 * carries (the whole fleet, or the caller's own store). This is the single
 * reconcile point for the subscription contract: adjust the type +
 * `normalizeOmniSubscription` here and the control follows.
 */
export type OmniSubscription = {
  subscribed: boolean;
  cadence: 'daily';
  scope: 'fleet' | 'store';
  auto_enrolled: boolean;
  status: 'active' | 'unsubscribed';
};

/** The PUT body to change the subscription. Cadence is daily for now. */
export type OmniSubscriptionUpdate = {
  subscribed: boolean;
  cadence?: 'daily';
};

export const OMNI_SUBSCRIPTION_API_BASE = `${OMNI_API_BASE}/subscription`;
export const OMNI_SUBSCRIPTION_QUERY_KEY = ['omni-subscription'] as const;

/**
 * Reads a raw subscription payload into the typed shape, defensively. Returns
 * null on any shape mismatch (missing/invalid `subscribed`), which the hook and
 * control treat as "not available yet" so the page degrades gracefully while
 * the api backend ships in parallel. `cadence` is normalized to 'daily';
 * `scope` and `status` fall back to their primary values on an unknown value.
 */
export function normalizeOmniSubscription(raw: unknown): OmniSubscription | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.subscribed !== 'boolean') return null;
  return {
    subscribed: r.subscribed,
    cadence: 'daily',
    scope: r.scope === 'store' ? 'store' : 'fleet',
    auto_enrolled: r.auto_enrolled === true,
    status: r.status === 'unsubscribed' ? 'unsubscribed' : 'active',
  };
}

export type UseOmniSubscriptionResult = {
  /** The caller's subscription, or null when the endpoint is unavailable. */
  subscription: OmniSubscription | null;
  /** True once the GET has resolved to a valid subscription shape. */
  available: boolean;
  isLoading: boolean;
  error: Error | null;
  /** Writes the new state via PUT. `subscribed: false` records an unsubscribe. */
  save: (subscribed: boolean, opts?: { onError?: (e: Error) => void; onSuccess?: () => void }) => void;
  isSaving: boolean;
};

/**
 * React Query hook for the OMNI daily-email subscription. Reads current state
 * and exposes a `save` that PUTs a change and reconciles the cache from the
 * response. `enabled` gates the fetch (mount only for callers who can view the
 * report). `retry: false` plus the tolerant `normalizeOmniSubscription` read
 * mean a 404 / 503 / shape mismatch surfaces as `available === false` rather
 * than a crash, so the control simply hides, matching the rest of the page.
 */
export function useOmniSubscription(enabled: boolean): UseOmniSubscriptionResult {
  const qc = useQueryClient();
  const query = useQuery<OmniSubscription | null, Error>({
    queryKey: OMNI_SUBSCRIPTION_QUERY_KEY,
    queryFn: async () => normalizeOmniSubscription(await apiGet<unknown>(OMNI_SUBSCRIPTION_API_BASE)),
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
  });

  const mutation = useMutation<OmniSubscription | null, Error, boolean>({
    mutationFn: async (subscribed: boolean) => {
      const body: OmniSubscriptionUpdate = subscribed
        ? { subscribed: true, cadence: 'daily' }
        : { subscribed: false };
      return normalizeOmniSubscription(await apiPut<unknown>(OMNI_SUBSCRIPTION_API_BASE, body));
    },
    onSuccess: (data) => {
      if (data) qc.setQueryData<OmniSubscription | null>(OMNI_SUBSCRIPTION_QUERY_KEY, data);
      else void qc.invalidateQueries({ queryKey: OMNI_SUBSCRIPTION_QUERY_KEY });
    },
  });

  const subscription = query.data ?? null;
  return {
    subscription,
    available: !query.isLoading && !query.error && subscription !== null,
    isLoading: query.isLoading,
    error: query.error,
    save: (subscribed, opts) =>
      mutation.mutate(subscribed, { onError: opts?.onError, onSuccess: () => opts?.onSuccess?.() }),
    isSaving: mutation.isPending,
  };
}

/**
 * Triggers a browser download of the OMNI report Excel export.
 * Calls GET /v1/retail/omni/export?as_of=YYYY-MM-DD and saves the blob.
 * Mirrors downloadRetailXlsx / downloadAllocationXlsx.
 */
export async function downloadOmniXlsx(asOf: string | null): Promise<void> {
  const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : '';
  const url = `${OMNI_API_BASE}/export${qs}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const filename = m?.[1] ?? `omni-performance-${asOf ?? 'export'}.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------------------------------------------------------------------------
// Normalizer: flatten the grouped response into display rows
// ---------------------------------------------------------------------------

export type OmniRowKind = 'store' | 'total';

export type OmniDisplayRow = {
  kind: OmniRowKind;
  key: string;
  /** Store number, or the fleet-total label. */
  label: string;
  /** Store name, shown as a secondary label on store rows. */
  sublabel?: string;
  city?: string;
  state?: string;
  sections: OmniSections;
};

/**
 * Flattens the OMNI response into a single ordered list of rows: one store row
 * per store, in the order the API returns them (by store_no), followed by a
 * single fleet-total row. A store nests its sections under `.sections`, while
 * `grandTotal` IS a `<sections>` object directly. Store scope is enforced
 * server-side, so a store-only caller's response simply contains fewer stores.
 *
 * This is the single reconcile point for response-shape drift.
 */
export function flattenOmni(
  stores: OmniStore[],
  grandTotal: OmniSections | null,
): OmniDisplayRow[] {
  const rows: OmniDisplayRow[] = [];
  for (const s of stores ?? []) {
    rows.push({
      kind: 'store',
      key: `store:${s.store_no}`,
      label: s.store_no,
      sublabel: s.store_name,
      city: s.city ?? '',
      state: s.state ?? '',
      sections: s.sections ?? {},
    });
  }
  if (grandTotal) {
    rows.push({ kind: 'total', key: 'total', label: 'Fleet total', sections: grandTotal });
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Section + column configuration
// ---------------------------------------------------------------------------

export const OMNI_SECTIONS: { key: OmniSectionKey; label: string; blurb: string }[] = [
  { key: 'sales', label: 'Sales', blurb: 'Net sales vs the same weekday last year.' },
  { key: 'transactions', label: 'Transactions', blurb: 'Transaction count vs the same weekday last year.' },
  { key: 'traffic', label: 'Traffic & Conversion', blurb: 'Door traffic and conversion (transactions / traffic).' },
  { key: 'units', label: 'Units', blurb: 'Units sold vs the same weekday last year.' },
  {
    key: 'dpt_upt',
    label: 'Dollars Per Transaction (DPT) & Units Per Transaction (UPT)',
    blurb: 'Dollars per transaction (DPT) and units per transaction (UPT), vs the same weekday last year.',
  },
];

/**
 * Period labels spell out WTD/MTD/YTD with the acronym in parentheses, since the
 * group header has room for it (it spans a whole section's columns). Yesterday
 * has no acronym to expand. The narrow mobile-card period header still shows
 * the bare short form (`OMNI_PERIOD_SHORT` in Performance.tsx) and reads this
 * full label from its own tooltip.
 */
export const OMNI_PERIODS: { key: OmniPeriodKey; label: string }[] = [
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'wtd', label: 'Week to Date (WTD)' },
  { key: 'mtd', label: 'Month to Date (MTD)' },
  { key: 'ytd', label: 'Year to Date (YTD)' },
];

/** How a single value column reads its number from a flattened block. */
export type OmniValueKind = 'money' | 'int' | 'rate' | 'dpt' | 'upt' | 'comp';

export type OmniValueCol = {
  label: string;
  /** Key into the flattened block produced by `blockFor`. */
  field: string;
  kind: OmniValueKind;
  /**
   * Tooltip shown on the header cell for a column whose visible label is a
   * bare acronym kept short because the column itself is a narrow numeric
   * cell (TY, DPT, UPT). Omitted for columns whose label is already spelled
   * out, or whose date already carries the LY (cal) / LY (wk) tooltip.
   */
  title?: string;
};

/**
 * The value columns shown for each section, inside every period group. Each
 * section leads with its this-year metric(s), then the same weekday last year
 * and the colored comp ratio between them. Field names index into the flattened
 * block from `blockFor` (nested metrics like traffic conversion and dpt/upt are
 * flattened to prefixed scalar keys).
 *
 * The calendar-date columns and the Plan / Bgt % columns were removed on
 * 2026-08-12, so every section is three columns per period rather than five to
 * seven.
 */
export const OMNI_SECTION_COLUMNS: Record<OmniSectionKey, OmniValueCol[]> = {
  sales: [
    { label: 'TY', field: 'ty', kind: 'money', title: 'This Year (TY)' },
    { label: 'LY', field: 'ly_week', kind: 'money', title: 'Last Year (LY), the same weekday' },
    { label: 'vs LY', field: 'comp_week', kind: 'comp' },
  ],
  transactions: [
    { label: 'TY', field: 'ty', kind: 'int', title: 'This Year (TY)' },
    { label: 'LY', field: 'ly_week', kind: 'int', title: 'Last Year (LY), the same weekday' },
    { label: 'vs LY', field: 'comp_week', kind: 'comp' },
  ],
  traffic: [
    { label: 'Traffic', field: 'ty', kind: 'int' },
    { label: 'Conv %', field: 'conv_ty', kind: 'rate' },
    { label: 'LY', field: 'ly_week', kind: 'int', title: 'Last Year (LY), the same weekday' },
    { label: 'vs LY', field: 'comp_week', kind: 'comp' },
  ],
  units: [
    { label: 'TY', field: 'ty', kind: 'int', title: 'This Year (TY)' },
    { label: 'LY', field: 'ly_week', kind: 'int', title: 'Last Year (LY), the same weekday' },
    { label: 'vs LY', field: 'comp_week', kind: 'comp' },
  ],
  // DPT & UPT: each derived rate shown with its TY plus its last-year comp.
  // Column headers stay as the bare DPT/UPT short form (the columns are narrow
  // numeric cells); the spelled-out form rides each header's tooltip instead,
  // while the section chip label above spells both out in full.
  dpt_upt: [
    { label: 'DPT', field: 'dpt_ty', kind: 'dpt', title: 'Dollars Per Transaction (DPT)' },
    {
      label: 'DPT vs LY',
      field: 'dpt_comp_week',
      kind: 'comp',
      title: 'Dollars Per Transaction (DPT) vs the same weekday last year',
    },
    { label: 'UPT', field: 'upt_ty', kind: 'upt', title: 'Units Per Transaction (UPT)' },
    {
      label: 'UPT vs LY',
      field: 'upt_comp_week',
      kind: 'comp',
      title: 'Units Per Transaction (UPT) vs the same weekday last year',
    },
  ],
};

// ---------------------------------------------------------------------------
// Block flattening (nested contract -> flat bag the grid reads)
// ---------------------------------------------------------------------------

/** A flattened, scalar-only view of one section+period block, keyed by column field. */
export type OmniBlock = Record<string, number | null>;

function num(v: unknown): number | null {
  return typeof v === 'number' && !Number.isNaN(v) ? v : null;
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

/**
 * Flattens one raw section+period block into the scalar bag the grid renders.
 * Traffic's nested `conversion` and dpt_upt's nested `dpt` / `upt` metrics are
 * expanded to prefixed keys (`conv_ty`, `dpt_ty`, `dpt_comp_week`, ...). Reads
 * defensively so a partial block degrades to "--".
 */
function flattenBlock(section: OmniSectionKey, raw: Record<string, unknown>): OmniBlock {
  const base: OmniBlock = {
    ty: num(raw.ty),
    ly_week: num(raw.ly_week),
    comp_week: num(raw.comp_week),
  };
  if (section === 'traffic') {
    const c = obj(raw.conversion);
    return {
      ...base,
      conv_ty: num(c.ty),
      conv_comp_week: num(c.comp_week),
      // Count of days in the period that had traffic data; conversion covers
      // only those days. Shown next to the conversion figure in the grid.
      conv_days: num(raw.days_with_traffic),
    };
  }
  if (section === 'dpt_upt') {
    const d = obj(raw.dpt);
    const u = obj(raw.upt);
    return {
      dpt_ty: num(d.ty),
      dpt_comp_week: num(d.comp_week),
      upt_ty: num(u.ty),
      upt_comp_week: num(u.comp_week),
    };
  }
  return base; // sales, transactions, units
}

/**
 * Reads and flattens a section's period block from a display row, tolerating
 * absent sections/periods. Returns null when the block is missing.
 */
export function blockFor(
  sections: OmniSections,
  section: OmniSectionKey,
  period: OmniPeriodKey,
): OmniBlock | null {
  const periods = sections?.[section];
  if (!periods) return null;
  const raw = periods[period];
  if (!raw) return null;
  return flattenBlock(section, obj(raw));
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** Formats an integer count as "1,240". Null/NaN render "--". */
export function formatInt(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '--';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(v);
}

/** Formats a units-per-transaction value to one decimal: "2.4". Null/NaN "--". */
export function formatUpt(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '--';
  return v.toFixed(1);
}

/**
 * Formats an unsigned rate (conversion, budget attainment) as "23.4%".
 * A decimal ratio in, so 0.234 -> "23.4%". Null/NaN render "--".
 */
export function formatRate(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '--';
  return `${(v * 100).toFixed(1)}%`;
}

/** Formats a block field value per its column kind. */
export function formatOmniValue(v: number | null | undefined, kind: OmniValueKind): string {
  switch (kind) {
    case 'money': return formatDollars(v);
    case 'int':   return formatInt(v);
    case 'rate':  return formatRate(v);
    case 'dpt':   return formatDpt(v);
    case 'upt':   return formatUpt(v);
    case 'comp':  return formatComp(v);
  }
}

// ---------------------------------------------------------------------------
// Header dates: the actual calendar dates behind each period column-group and
// the LY basis, read from the `windows` the API already computes (never
// recomputed client-side; see the fiscal-week, fiscal-year and 364-day-shift
// logic in the api repo's tools/retail/src/omni-metrics.ts).
// ---------------------------------------------------------------------------

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_LONG = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];
const MONTH_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Splits a "2026-08-07" string into [year, month, day], or null if malformed. */
function isoParts(iso: string): [number, number, number] | null {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return [y, m, d];
}

/** The day of week (0 = Sunday) for a "YYYY-MM-DD" date, computed in UTC so the
 * local browser time zone never shifts a calendar date by a day. */
function isoWeekday(iso: string): number | null {
  const parts = isoParts(iso);
  if (!parts) return null;
  const [y, m, d] = parts;
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** "8/7/25" - compact month/day/2-digit-year, for a last-year header sub-line. */
export function omniShortDate(iso: string): string {
  const parts = isoParts(iso);
  if (!parts) return iso;
  const [y, m, d] = parts;
  return `${m}/${d}/${String(y).slice(-2)}`;
}

/** "Sat 8/2/25" - short weekday plus the compact last-year date. */
export function omniShortWeekdayDate(iso: string): string {
  const wd = isoWeekday(iso);
  if (wd == null) return omniShortDate(iso);
  return `${WEEKDAY_SHORT[wd]} ${omniShortDate(iso)}`;
}

/** "Friday, August 7, 2026" - the full date, for the Yesterday group tooltip. */
export function omniLongDate(iso: string): string {
  const parts = isoParts(iso);
  const wd = isoWeekday(iso);
  if (!parts || wd == null) return iso;
  const [y, m, d] = parts;
  return `${WEEKDAY_LONG[wd]}, ${MONTH_LONG[m - 1]} ${d}, ${y}`;
}

/** "Sun 8/2 - Fri 8/7" - the this-year range style for the WTD/MTD group tooltips.
 * No year, since the range is always the current report year. */
export function omniShortWeekdayRange(startIso: string, endIso: string): string {
  const start = isoParts(startIso);
  const end = isoParts(endIso);
  const startWd = isoWeekday(startIso);
  const endWd = isoWeekday(endIso);
  if (!start || !end || startWd == null || endWd == null) return `${startIso} - ${endIso}`;
  const startStr = `${WEEKDAY_SHORT[startWd]} ${start[1]}/${start[2]}`;
  const endStr = `${WEEKDAY_SHORT[endWd]} ${end[1]}/${end[2]}`;
  return `${startStr} - ${endStr}`;
}

/** "Sun 7/27/25 - Sat 8/2/25" - a weekday last-year date range, for the LY (wk) tooltip. */
export function omniShortWeekdayDateRange(startIso: string, endIso: string): string {
  if (startIso === endIso) return omniShortWeekdayDate(startIso);
  return `${omniShortWeekdayDate(startIso)} - ${omniShortWeekdayDate(endIso)}`;
}

/**
 * The hover tooltip for a period column-group header (Yesterday / WTD / MTD):
 * the long date for Yesterday, since its window is a single day, or the short
 * weekday range for WTD/MTD. Null when the API has not sent `windows` (an
 * older API, or no data at all), in which case the header shows no tooltip.
 */
export function omniGroupTooltip(
  period: OmniPeriodKey,
  windows: OmniWindows | null | undefined,
): string | null {
  const block = windows?.[period];
  if (!block) return null;
  const { start, end } = block.ty;
  return period === 'yesterday' ? omniLongDate(start) : omniShortWeekdayRange(start, end);
}

/**
 * The compact date shown under the "LY" header label: the single date for
 * Yesterday, or the range's END date for WTD/MTD/YTD (the full range rides the
 * header's tooltip via `omniLyColumnTooltip`). Null when the API has not sent
 * the basis.
 *
 * The date carries its weekday, which is the point of this basis: the weekday
 * matches this year's, so the reader can see the comparison is like for like
 * without doing the arithmetic (Steven, 2026-08-08).
 */
export function omniLyColumnDate(
  windows: OmniWindows | null | undefined,
  period: OmniPeriodKey,
): string | null {
  const w = windows?.[period]?.lyWeek;
  if (!w) return null;
  return omniShortWeekdayDate(w.end);
}

/**
 * The tooltip for the "LY" header: the full date range, shown only when the
 * basis actually spans more than one day (WTD/MTD/YTD). Null for Yesterday,
 * where the single date is already the whole story, and null when the API has
 * not sent the basis.
 */
export function omniLyColumnTooltip(
  windows: OmniWindows | null | undefined,
  period: OmniPeriodKey,
): string | null {
  const w = windows?.[period]?.lyWeek;
  if (!w || w.start === w.end) return null;
  return omniShortWeekdayDateRange(w.start, w.end);
}
